import {
  Box,
  Divider,
  IconButton,
  Typography,
  TextField,
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  CircularProgress,
  Switch,
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import Ask7G from "../../assets/client/magic-icon.svg";
import SearchIcon from "../../assets/common/search.svg";
import AccountIcon from "../../assets/navigations/Accounts.svg";
import SendIcon from "@mui/icons-material/Send";
import "../../styles/clientPage.css";
import "../../styles/interactionDetailsPage.css";
import GlobalSearchService from "../../services/integrations/GlobalSearchService";
import useDisplaySnackbar from "../../utils/useDisplaySnackbar";
import ClientService from "../../services/clients/ClientService";
import { capitalizeFirstLetter, formatDateLabel, isSameCalendarDay, useDebounce } from "../../utils/utils";
import redXIcon from "../../assets/common/red_x_icon.svg";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import EditIcon from "../../assets/client/ask7g_edit_icon.svg";
import { AllClientsOption, LevelAccessType } from "../../utils/types";
import useGlobalContext from "../../context/useGlobalContext";
import "../../styles/NavigationSlideOver.css";

type Ask7GMessage = {
  sender: "user" | "bot";
  text: string;
  createdAt: number;
  isHistory: boolean;
};

type Ask7GHistoryItem = {
  id: string;
  title: string;
  subtitle?: string;
  updatedAt: number;
  latestAnswer?: string;
  subLevelId?: string;
  levelType?: "client" | "account";
  isAllClients?: boolean;
};

interface ClientAsk7GProps {
  view?: "icon" | "full";
  isExpanded?: boolean;
  onToggleExpand?: (expanded: boolean) => void;
  isSpaceMode?: boolean;
}

const ALL_CLIENTS_LABEL = "All Clients";

const isAllClientsHistoryItem = (
  item: any,
  accountId?: string
): boolean => {
  if (!item) return false;
  const name = (item.clientName || item.title || "")
    .trim()
    .toLowerCase();

  return (
    item.levelType === "account" ||
    name === "all clients" ||
    (Boolean(accountId) &&
      (item.levelId === accountId || item.id === accountId) &&
      !item.subLevelId)
  );
};

const ClientAsk7G = ({ view, isExpanded: propIsExpanded, onToggleExpand, isSpaceMode = false }: ClientAsk7GProps) => {
  const { spaceList, accountDetails } = useGlobalContext();
  const allUserGroupSpaceIds = useMemo(
    () => spaceList?.map((space) => space.value) || [],
    [spaceList]
  );
  const spaceIds = isSpaceMode ? allUserGroupSpaceIds : undefined;

  const [internalExpanded, setInternalExpanded] = useState(false);
  const isExpanded = propIsExpanded !== undefined ? propIsExpanded : internalExpanded;

  const setIsExpanded = (val: boolean) => {
    if (onToggleExpand) {
      onToggleExpand(val);
    } else {
      setInternalExpanded(val);
    }
  };
    const [selectedClientId, setSelectedClientId] = useState("");
    const isSearchAcrossClients = selectedClientId === AllClientsOption.ALL_CLIENTS;
    const levelAccessType: LevelAccessType = isSearchAcrossClients
      ? "account"
      : isSpaceMode
      ? "client"
      : "hierarchy";
    const [input, setInput] = useState("");
    const [messages, setMessages] = useState<Ask7GMessage[]>([]);
    const [loading, setLoading] = useState(false);
    const messageContainerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const latestQuestionRef = useRef<HTMLDivElement>(null);
    const isAtBottomRef = useRef(true);
    const { showSnackbar } = useDisplaySnackbar();
    const [query, setQuery] = useState("");
    const loadingText = "Thinking...";
    const isFirstQuestion = messages.length === 1 && messages[0]?.sender === "user";
    const [selectedClient, setSelectedClient] = useState<any>(null);
    const [accountSearch, setAccountSearch] = useState("");
    const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
    const [accountMenuPlacement, setAccountMenuPlacement] = useState<"above" | "below">("above");
    const [showAccountError, setShowAccountError] = useState(false);
    const [selectedHistoryId, setSelectedHistoryId] = useState("");
    const [historyList, setHistoryList] = useState<Ask7GHistoryItem[]>([]);
    const [selectedHistoryParams, setSelectedHistoryParams] = useState({
      id: "",
      subLevelId: "",
    });
    const {featureMap} = useGlobalContext();
    const isClientAsk7GAllClientsOptionEnabled = featureMap?.client_ask_7g_all_clients === true;

    const accountId = accountDetails?.accountId || "";
    const historyLevelId = isSearchAcrossClients
      ? accountId
      : selectedHistoryParams.id;

    const historySubLevelId = isSearchAcrossClients
      ? undefined
      : selectedHistoryParams.subLevelId;

    const renderDateSeparator = (label: string) => (
      <Box className="ask7g-date-separator" role="separator" aria-label={label}>
        <Box className="ask7g-date-separator-line" />
        <Typography className="ask7g-date-separator-text">{label}</Typography>
        <Box className="ask7g-date-separator-line" />
      </Box>
    );

    const {
      isSuccess: isClientAsk7GSuccess,
      isError: isClentAsk7GError,
      error: clientAsk7GError,
      data: clientAsk7gData,
      refetch: refetchClientsWithAsk7GHistory,
    } = GlobalSearchService.useGetClientsWithAsk7GHistory(
      {
        levelType: "client",
      },
      isExpanded
    );

    const isAsk7GHistoryEnabled = isSearchAcrossClients
      ? Boolean(historyLevelId)
      : Boolean(
          selectedHistoryParams.id && selectedHistoryParams.subLevelId
        );

    const {
      isLoading: isAsk7GHistoryLoading,
      isFetching: isAsk7GHistoryFetching,
      isSuccess: isAsk7GHistorySuccess,
      isError: isAsk7GHistoryError,
      error: ask7GHistoryError,
      data: ask7GHistoryData

    } = GlobalSearchService.useGetAsk7GHistory({
      levelType: isSearchAcrossClients ? "account" : "client",
      subLevelId: historySubLevelId,
      levelId: historyLevelId,
    },
      isAsk7GHistoryEnabled
    );

    const debouncedAccountSearch = useDebounce(accountSearch, 500);
    const isHierarchy = !isSpaceMode;
    const orgNameQuery = isHierarchy ? debouncedAccountSearch : undefined;

    const {
        isLoading: isAccountsLoading,
        isFetching: isAccountsFetching,
        isError: isAccountsError,
        error: accountsError,
        data: AccountsData,
    } = ClientService.useGetActiveClients(
        isExpanded,
        isSpaceMode,
        spaceIds,
        orgNameQuery,
        isHierarchy
    );

    const isAccountsSearchLoading =
        isHierarchy &&
        ((isAccountsLoading || isAccountsFetching) ||
        (accountSearch.trim() !== debouncedAccountSearch));
    
    useEffect(() => {
        if (isAccountsError) {
            showSnackbar("error", accountsError?.message || "Failed to load data.");
        }   
    }, [isAccountsError, accountsError]);

    const clientList = useMemo(() => {
        return (AccountsData?.data || [])?.map((c: any) => ({
            ...c,
            clientNameLower: (c.clientName || "")?.toLowerCase()
        }));
    }, [AccountsData?.data]);

    const isHierarchySearchReady =
        isHierarchy &&
        Boolean(accountSearch.trim()) &&
        accountSearch.trim() === debouncedAccountSearch &&
        !isAccountsFetching;
    const isClientSearchActive = Boolean(accountSearch.trim());

    useEffect(()=> {
      if (isClientAsk7GSuccess && clientAsk7gData) {
        const history: Ask7GHistoryItem[] =
          clientAsk7gData?.map((item: any) => {
            const isAllClients = isAllClientsHistoryItem(item, accountId);
            return {
              id: item.levelId,
              title: isAllClients ? ALL_CLIENTS_LABEL : capitalizeFirstLetter(item.clientName),
              subtitle: item.latestQuestion,
              updatedAt: Date.now(),
              latestAnswer: item.latestAnswer,
              subLevelId: item.sublevelId || "",
              levelType: (item.levelType || (isAllClients ? "account" : "client")) as "client" | "account",
              isAllClients,
            };
          }) || [];

        setHistoryList(history);
        if (selectedClient) {
          const matchingHistory = isSearchAcrossClients
              ? history.find((item) => item.isAllClients)
              : history.find(
                  (item: any) =>
                    item.id === selectedClient.parentOrgId &&
                    item.subLevelId === selectedClient.clientOrgId
                );

          if (matchingHistory) {
            if (
              selectedHistoryParams.id !== matchingHistory.id ||
              selectedHistoryParams.subLevelId !== matchingHistory.subLevelId
            ) {
              setSelectedHistoryId(matchingHistory.id);
              setSelectedHistoryParams({
                id: matchingHistory.id,
                subLevelId: matchingHistory.subLevelId || "",
              });
            }
          }
        }         
      } else if(isClentAsk7GError){
        const errorMessage =
        clientAsk7GError?.response?.data?.message || clientAsk7GError?.message || "Failed to load clients.";
        showSnackbar("error", errorMessage);
      }
    }, [clientAsk7gData, isClentAsk7GError, isClientAsk7GSuccess, selectedClient, selectedHistoryId, isSearchAcrossClients, accountId])

    const filteredClientList = useMemo(() => {
        const search = accountSearch?.trim()?.toLowerCase();
        if (!search) {
          return [];
        }

        if (isHierarchy) {
          return isHierarchySearchReady ? clientList : [];
        }
        return clientList
        .filter((client: any) =>
          client.clientNameLower.includes(search)
        )
        .sort((a: any, b: any) => {
          const aName = a.clientNameLower;
          const bName = b.clientNameLower;

          const aStartsWith = aName.startsWith(search);
          const bStartsWith = bName.startsWith(search);

          // 1. Names starting with the search text come first
          if (aStartsWith && !bStartsWith) return -1;
          if (!aStartsWith && bStartsWith) return 1;

          // 2. Alphabetical order within each group
          return aName.localeCompare(bName);
        });
    }, [clientList, accountSearch, isHierarchy, isHierarchySearchReady]);

    const visibleClientList = useMemo(() => {
      if (isClientSearchActive) {
        return filteredClientList;
      }

      const hasSelectedClient =
        selectedClientId &&
        selectedClientId !== AllClientsOption.ALL_CLIENTS &&
        selectedClient;

      const selectedClientIsVisible = filteredClientList.some(
        (client: any) => client.clientOrgId === selectedClientId
      );

      return hasSelectedClient && !selectedClientIsVisible
        ? [selectedClient, ...filteredClientList]
        : filteredClientList;
    }, [filteredClientList, isClientSearchActive, selectedClient, selectedClientId]);

    const shouldShowNoClientsFound =
        !isAccountsSearchLoading &&
        !isAccountsLoading &&
        !isAccountsFetching &&
        Array.isArray(AccountsData?.data) &&
        (AccountsData.data.length === 0 ||
          (Boolean(accountSearch.trim()) && visibleClientList.length === 0));

    const {
        data,
        isSuccess,    
        isLoading,
        isFetching,
        isError,
        error,
    } = GlobalSearchService.useGetAsk7GInteractionQuery(
        {
          query: query,
          orgId: isSearchAcrossClients ? undefined : selectedClient?.parentOrgId,
          subOrgId: isSearchAcrossClients ? undefined : selectedClient?.clientOrgId,
          levelAccessType,
        },
        !!query
    );

    useEffect(() => {
        setLoading(isLoading || isFetching)
        if (isSuccess) {
        setMessages((prev) => [
            ...prev,
            { sender: "bot", text: data?.data?.answer ?? "No response found.", createdAt: Date.now(), isHistory: false },
        ]);
        setQuery("");
        refetchClientsWithAsk7GHistory?.();
        } else if(isError){
          const errorMessage =
            error?.response?.data?.message || error?.message || "Something went wrong";
            showSnackbar("error", errorMessage)
            setQuery("");
        }
    }, [data, isLoading, isFetching, isError, error]);
    

    useEffect(() => {
        // Focus when component mounts or when loading completes
        if (!loading) {
        inputRef.current?.focus();
        }
    }, [loading]);    
    const handleAsk = () => {
        if (!input.trim() || loading) return;
        if (!isSearchAcrossClients && (!selectedClient?.parentOrgId || !selectedClient?.clientOrgId)) {
          setShowAccountError(true);
          setIsAccountMenuOpen(true);
          return;
        }
        const question = input.trim();
        const createdAt = Date.now();

        // Add user message
        setMessages((prev) => [...prev, { sender: "user", text: question, createdAt, isHistory: false }]);

        // Trigger API call
        setQuery(question);
        setInput("");
        setShowAccountError(false);
    };

    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") handleAsk();
    };

    const handleClientChange = (event: SelectChangeEvent<string>) => {
      const selectedId = event.target.value;
      if (!selectedId) return;

      setSelectedClientId(selectedId);
      setIsAccountMenuOpen(false);
      setAccountSearch("");
      setShowAccountError(false);
      setQuery("");

      if (selectedId === AllClientsOption.ALL_CLIENTS) {
        setSelectedClient({
          clientOrgId: AllClientsOption.ALL_CLIENTS,
          clientName: ALL_CLIENTS_LABEL,
        });

        const matchingHistory = historyList.find((history) =>
          isAllClientsHistoryItem(history, accountId)
        );

        if (matchingHistory) {
          setSelectedHistoryId(matchingHistory.id);
          setSelectedHistoryParams({
            id: accountId,
            subLevelId: "",
          });
        } else {
          setSelectedHistoryId("");
          setSelectedHistoryParams({
            id: accountId,
            subLevelId: "",
          });
        }
        setMessages([]);
        return;
      }

      const chosenClient =
        clientList.find((client: any) => client?.clientOrgId === selectedId) ||
        (selectedClient && selectedClient.clientOrgId === selectedId ? selectedClient : null);

      if (chosenClient) {
        setSelectedClient(chosenClient);
      }

      const activeClient = chosenClient || selectedClient;
      if (!activeClient) return;

      const matchingHistory = historyList.find(
        (history) =>
          history.id === activeClient.parentOrgId &&
          history.subLevelId === activeClient.clientOrgId
      );

      if (matchingHistory) {
        setSelectedHistoryId(matchingHistory.id);
        setSelectedHistoryParams({
          id: matchingHistory.id,
          subLevelId: matchingHistory.subLevelId || "",
        });
      } else {
        setSelectedHistoryId("");
        setSelectedHistoryParams({
          id: "",
          subLevelId: "",
        });
        setMessages([]);
      }
    };

    useEffect(() => {
      if ((isSpaceMode || !isClientAsk7GAllClientsOptionEnabled) && isSearchAcrossClients) {
        setSelectedClientId("");
        setSelectedClient(null);
        setSelectedHistoryId("");
        setSelectedHistoryParams({
          id: "",
          subLevelId: "",
        });
        setMessages([]);
        setQuery("");
      }
    }, [isSpaceMode, isClientAsk7GAllClientsOptionEnabled, isSearchAcrossClients]);

    useEffect(() => {
      if (!input.trim()) {
        setShowAccountError(false);
      }
    }, [input]);

    useEffect(() => {
    if (!historyLevelId) {
      return;
    }

    if (isAsk7GHistorySuccess && ask7GHistoryData) {
      const sortedHistory = [...ask7GHistoryData].sort(
        (a: any, b: any) =>
          new Date(a.createdAt).getTime() -
          new Date(b.createdAt).getTime()
      );  

      const formattedMessages: Ask7GMessage[] = sortedHistory.flatMap((item: any) => [
        {
          sender: "user" as const,
          text: item?.question ?? "",
          createdAt: new Date(item?.createdAt).getTime(),
          isHistory: true,
        },
        {
          sender: "bot" as const,
          text: item?.answer ?? "",
          createdAt: new Date(item?.createdAt).getTime(),
          isHistory: true,
        },
      ]);
      setMessages(formattedMessages);
    } else if (isAsk7GHistoryError) {
      const errorMessage = ask7GHistoryError?.response?.data?.message || ask7GHistoryError?.message || "Failed to load history.";
      showSnackbar("error", errorMessage);
      }
    }, [
      ask7GHistoryData,
      isAsk7GHistoryError,
      isAsk7GHistorySuccess,
      isAsk7GHistoryLoading,
      isAsk7GHistoryFetching,
      historyLevelId,
    ])

    const handleAllClientsToggle = (checked: boolean) => {
      if (checked) {
        handleClientChange({
          target: { value: AllClientsOption.ALL_CLIENTS },
        } as SelectChangeEvent<string>);
        return;
      }

      setSelectedClientId("");
      setSelectedClient(null);
      setSelectedHistoryId("");
      setSelectedHistoryParams({ id: "", subLevelId: "" });
      setMessages([]);
      setQuery("");
      setShowAccountError(false);
      setIsAccountMenuOpen(false);
      setAccountSearch("");
    };

    const renderAccountSelector = () => (
      <Box
        className={`ask7g-account-picker ${selectedClientId ? "has-selected" : ""} ${showAccountError ? "account-error" : ""} ${isAccountMenuOpen ? "is-open" : ""}`}
      >
        <img src={AccountIcon} className="right-icon ask7g-account-picker-icon" alt="" />
        <FormControl size="small" variant="standard" sx={{ minWidth: 100 }}>
          <Select
            className="ask7g-account-select"
            value={selectedClientId}
            onChange={handleClientChange}
            open={isAccountMenuOpen}
            onOpen={(event) => {
              const anchor = event.currentTarget as HTMLElement;
              const anchorRect = anchor.getBoundingClientRect();
              const availableBelow = window.innerHeight - anchorRect.bottom;
              setAccountMenuPlacement(availableBelow >= 320 ? "below" : "above");
              setIsAccountMenuOpen(true);
            }}
            onClose={() => {
              setIsAccountMenuOpen(false);
              setAccountSearch("");
              if (input.trim() && !selectedClientId) {
                setShowAccountError(true);
              }
            }}
            displayEmpty
            disableUnderline
            renderValue={(selected) => {
              if (!selected) {
                return (
                  <Box component="span" className="ask7g-account-placeholder">
                    Select Client
                    <Box component="span" className="ask7g-account-required">
                      *
                    </Box>
                  </Box>
                );
              }

              if (selected === AllClientsOption.ALL_CLIENTS) {
                return ALL_CLIENTS_LABEL;
              }

              return capitalizeFirstLetter(selectedClient?.clientName) || "Select Client";
            }}
            MenuProps={{
              autoFocus: false,
              anchorOrigin: {
                vertical: accountMenuPlacement === "above" ? "top" : "bottom",
                horizontal: "center",
              },
              transformOrigin: {
                vertical: accountMenuPlacement === "above" ? "bottom" : "top",
                horizontal: "center",
              },
              PaperProps: {
                sx: {
                  mt: accountMenuPlacement === "below" ? 1 : 0,
                  boxShadow: "0 10px 30px rgba(53, 1, 58, 0.12)",
                  maxHeight: 320,
                  minWidth: "200px !important",
                },
              },
              MenuListProps: {
                sx: {
                  paddingTop: "0px",
                  paddingBottom: "0px",
                },
              },
            }}
            sx={{
              fontSize: "14px",
              padding: "8px 0",
              ".MuiSelect-select": {
                padding: 0,
                minHeight: "auto",
                display: "flex",
                alignItems: "center",
              },
            }}
          >
            <Box className="ask7g-account-search">
              <img src={SearchIcon} alt="search"/>
              <input
                autoFocus
                value={accountSearch}
                placeholder="Search Clients..."
                aria-label="Search clients"
                onClick={(event) => event.stopPropagation()}
                onKeyDown={(event) => event.stopPropagation()}
                onChange={(event) => setAccountSearch(event.target.value)}
              />
              {isAccountsSearchLoading && (
                <CircularProgress size={16} />
              )}
            </Box>

          {isClientAsk7GAllClientsOptionEnabled && !isSpaceMode && (
            <MenuItem
              value={AllClientsOption.ALL_CLIENTS}
              aria-hidden="true"
              sx={{ display: "none" }}
            />
          )}
          {isClientAsk7GAllClientsOptionEnabled && !isSpaceMode && (
            <Box
              className={`ask7g-account-all-row ${
                selectedClientId === AllClientsOption.ALL_CLIENTS ? "is-selected" : ""
              }`}
              onClickCapture={(event) => {
                const target = event.target as HTMLElement;
                if (target.closest(".ask7g-account-all-switch")) return;

                event.stopPropagation();
                handleAllClientsToggle(
                  selectedClientId !== AllClientsOption.ALL_CLIENTS
                );
              }}
            >
              <Typography className="ask7g-account-all-text">All Clients</Typography>
              <Switch
                className="ask7g-account-all-switch"
                inputProps={{ "aria-label": "Search across all clients" }}
                checked={selectedClientId === AllClientsOption.ALL_CLIENTS}
                onClick={(event) => event.stopPropagation()}
                onChange={(_, checked) => handleAllClientsToggle(checked)}
              />
            </Box>
          )}

            {visibleClientList.map((client: any) => {
              const optionValue = client.clientOrgId;

              return (
                <MenuItem
                  key={optionValue}
                  value={optionValue}
                  className="ask7g-client-option"
                >
                  {capitalizeFirstLetter(client.clientName)}
                  <Box
                    aria-hidden="true"
                    className={`ask7g-client-option-radio ${
                      selectedClientId === optionValue ? "is-selected" : ""
                    }`}
                  />
                </MenuItem>
              );
            })}
            {shouldShowNoClientsFound ? (
              <MenuItem disabled sx={{ borderRadius: "12px", opacity: 1 }}>
                No clients found
              </MenuItem>
            ) : null}
          </Select>
        </FormControl>
      </Box>
    );

    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.currentTarget;
      isAtBottomRef.current = target.scrollHeight - target.scrollTop - target.clientHeight < 80;
    };

    useEffect(() => {
      if (!messageContainerRef.current) return;
      const lastMsg = messages[messages.length - 1];

      if (lastMsg?.sender === "user") {
        isAtBottomRef.current = true;
        messageContainerRef.current.scrollTo({
          top: messageContainerRef.current.scrollHeight,
          behavior: "smooth",
        });
      } else if (lastMsg?.isHistory) {
        isAtBottomRef.current = true;
        messageContainerRef.current.scrollTop =
          messageContainerRef.current.scrollHeight;
      } else if (lastMsg?.sender === "bot" && !lastMsg.isHistory) {
        if (isAtBottomRef.current && latestQuestionRef.current) {
          messageContainerRef.current.scrollTo({
            top: Math.max(0, latestQuestionRef.current.offsetTop - 8),
            behavior: "smooth",
          });
        }
      }
    }, [messages]);

    const handleNewChat = () => {
      setMessages([]);
      setInput("");
      setQuery("");
      setLoading(false);
      setSelectedClientId("");
      setSelectedClient(null);
      setSelectedHistoryId("");
      setSelectedHistoryParams({
        id: "",
        subLevelId: "",
      });
      inputRef.current?.focus();
    };  
    
    const renderExpandedContent = () => (
    <Box
      className={`expanded-content ${isExpanded ? "visible" : ""} ${messages.length > 0 ? "has-messages" : ""}`}
      onClick={(event) => event.stopPropagation()}
    >
      <Box className="ask7g-split-layout">
        <Box className="ask7g-history-panel">
          <button
            className={`ask7g-history-newchat ${!selectedHistoryId ? "selected" : ""}`}
            onClick={handleNewChat}
            type="button"
          >
            <img src={EditIcon} alt="New Chat" className="ask7g-history-newchat-icon" />
            <span className="ask7g-history-nechat-text">New Chat</span>
          </button>

                  <Box className="ask7g-history-section-header">
                    <Typography className="ask7g-history-section-title">Recent Client Searches</Typography>
                  </Box>

                    <Box className="ask7g-history-list">
                      {historyList.map((item) => {
                        const isAllClients = Boolean(item.isAllClients);
                        const isSelected = isAllClients
                          ? (isSearchAcrossClients || item.id === selectedHistoryId)
                          : (!isSearchAcrossClients &&
                             item.id === selectedHistoryParams.id &&
                             item.subLevelId === selectedHistoryParams.subLevelId);
                        return (
                          <button
                            key={`${item.id}-${item.subLevelId || "all"}`}
                            className={`ask7g-history-item ${isSelected ? "selected" : ""}`}
                            onClick={() => {
                              setQuery("");
                              setSelectedHistoryId(item.id);

                              if (isAllClients) {
                                setSelectedClientId(AllClientsOption.ALL_CLIENTS);

                                setSelectedClient({
                                  clientOrgId: AllClientsOption.ALL_CLIENTS,
                                  clientName: ALL_CLIENTS_LABEL,
                                });

                                setSelectedHistoryParams({
                                  id: accountId,
                                  subLevelId: "",
                                });

                                setShowAccountError(false);
                                return;
                              }

                              setSelectedHistoryParams({
                                id: item.id,
                                subLevelId: item.subLevelId || "",
                              });
                              const matchingClient = clientList.find(
                                (client: any) =>
                                  client.parentOrgId === item.id &&
                                  client.clientOrgId === item.subLevelId
                              );
                              const clientToSelect = matchingClient || {
                                clientOrgId: item.subLevelId,
                                parentOrgId: item.id,
                                clientName: item.title,
                                clientNameLower: (item.title || "").toLowerCase(),
                              };
                              const clientId = clientToSelect.clientOrgId;
                              setSelectedClientId(clientId);
                              setSelectedClient(clientToSelect);
                              setShowAccountError(false);
                            }}
                            type="button"
                          >
                            <Box className="ask7g-history-item-row">
                              <Typography className="ask7g-history-item-title">{item.title}</Typography>
                              {item.subtitle && (
                                <Typography className="ask7g-history-item-subtitle">
                                  - {item.subtitle}
                                </Typography>
                              )}
                            </Box>
                          </button>
                        );
                      })}
                    </Box>
                  </Box>

                <Box className="ask7g-conversation-panel">
                  <Box className="ask7g-expanded-header ask7g-conversation-header">
                    <Box className="ask7g-conversation-header-left">
                    <img src={Ask7G} className="img-layout" alt="Ask 7G" style={{ width: 25, height: 25 }} />
                    <Typography className="ask7G-text ask7g-expanded-title">Ask 7G</Typography>
                    </Box>
                    <Box>
                      <div className="slide-over__header-left-close">
                        <button
                          type="button"
                          className="nso-close-btn"
                          onClick={() => setIsExpanded(false)}
                          aria-label="Close panel"
                        >
                          <img src={redXIcon} alt="" />
                        </button>
                      </div>
                    </Box>
                  </Box>

                  {messages.length === 0 ? (
                    <Box className="ask7g-empty-state">
                      <Box className="ask7g-expanded-input-row">
                        <TextField
                          fullWidth
                          placeholder="Type here"
                          value={input}
                          inputRef={inputRef}
                          onChange={(e) => setInput(e.target.value)}
                          className="ask7g-expanded-input"
                          onKeyDown={handleKeyPress}
                        />

                        <Box className="ask7g-expanded-actions">
                          {renderAccountSelector()}
                          <IconButton
                            size="small"
                            onClick={handleAsk}
                            disabled={!input.trim() || !selectedClientId || loading}
                            className="ask7g-send-button"
                            aria-label="Send question"
                          >
                            <SendIcon fontSize="small" className="ask7g-send-icon" />
                          </IconButton>
                        </Box>
                      </Box>
                    </Box>
                  ) : (
                    <Box className={`ask7g-chat-wrapper ask7g-chat-wrapper-full ${isFirstQuestion ? "first-question" : ""}`}>
                      <Box ref={messageContainerRef} onScroll={handleScroll} className="interaction-ask7g-messages ask7g-account ask7g-messages-fullwidth">
                        {messages.map((msg, index) => {
                          const prev = messages[index - 1];
                          const showDate = index === 0 || !prev || !isSameCalendarDay(prev.createdAt, msg.createdAt);
                          const isBoundaryAndAlreadyRendered =
                            prev?.isHistory &&
                            !msg.isHistory &&
                            !isSameCalendarDay(prev.createdAt, Date.now()) &&
                            isSameCalendarDay(msg.createdAt, Date.now());
                          const shouldRenderDate = showDate && !isBoundaryAndAlreadyRendered;

                          const isLastHistoryMessage = msg.isHistory && !messages[index + 1]?.isHistory;
                          const isLatestQuestion =
                            msg.sender === "user" &&
                            !msg.isHistory &&
                            index >= messages.length - 2;
                          return (
                            <Box
                              key={`${msg.createdAt}-${index}`}
                              ref={isLatestQuestion ? latestQuestionRef : undefined}
                            >
                              {shouldRenderDate && renderDateSeparator(formatDateLabel(msg.createdAt))}
                              <Box className="ask7g-messages-inner">
                                <Box
                                  className={`interaction-ask7g-message ${
                                    msg.sender === "user" ? "from-user" : "from-bot"
                                  }`}
                                >
                                  {msg.sender !== "user" && (
                                    <Box className="interaction-ask7g-icon-box">
                                      <img
                                        src={Ask7G}
                                        alt="Ask7G"
                                        className="interaction-ask7g-progress-icon"
                                      />
                                    </Box>
                                  )}
                                  <Box
                                    className={`interaction-ask7g-bubble ${
                                      msg.sender === "user" ? "question-bubble" : "response-bubble"
                                    }`}
                                  >
                                    <Typography variant="body2" className="interaction-ask7g-text">
                                      {msg.text}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Box>
                              {isLastHistoryMessage &&
                                !isSameCalendarDay(msg.createdAt, Date.now()) &&
                                renderDateSeparator(formatDateLabel(Date.now()))}
                            </Box>
                          );
                        })}

                        {loading && (
                          <Box>
                            {(() => {
                              const last = messages[messages.length - 1];
                              const now = Date.now();
                              const shouldShow =
                                !last || !isSameCalendarDay(last.createdAt, now);
                              return shouldShow ? renderDateSeparator(formatDateLabel(now)) : null;
                            })()}
                            <Box className="ask7g-messages-inner">
                              <Box className="interaction-ask7g-message from-bot">
                                <Box className="interaction-ask7g-loading-container inline">
                                  <Box className="interaction-ask7g-progress-wrapper">
                                    <svg width="0" height="0">
                                      <defs>
                                        <linearGradient
                                          id="clientAsk7gGradient"
                                          x1="2.52"
                                          y1="32.34"
                                          x2="33.6"
                                          y2="8.4"
                                          gradientUnits="userSpaceOnUse"
                                        >
                                          <stop offset="0.216892" stopColor="#35013A" stopOpacity="0.06" />
                                          <stop offset="0.995265" stopColor="#7F018B" />
                                        </linearGradient>
                                      </defs>
                                    </svg>

                                    <CircularProgress
                                      size={42}
                                      thickness={3}
                                      sx={{
                                        color: "transparent",
                                        position: "relative",
                                        "& .MuiCircularProgress-circle": {
                                          stroke: "url(#clientAsk7gGradient)",
                                        },
                                      }}
                                    />

                                    <img
                                      src={Ask7G}
                                      alt="Ask7G"
                                      className="interaction-ask7g-progress-icon"
                                    />
                                  </Box>

                                  <Typography variant="body2" className="interaction-ask7g-loading-text">
                                    {loadingText}
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                          </Box>
                        )}
                      </Box>

                      <Box className="ask7g-input-docked">
                        <Box className="ask7g-input-inner">
                          <Box className="ask7g-expanded-input-row">
                            <TextField
                              fullWidth
                              placeholder="Type here"
                              value={input}
                              inputRef={inputRef}
                              onChange={(e) => setInput(e.target.value)}
                              className="ask7g-expanded-input"
                              onKeyDown={handleKeyPress}
                            />

                            <Box className="ask7g-expanded-actions">
                              {renderAccountSelector()}
                              <IconButton
                                size="small"
                                onClick={handleAsk}
                                disabled={!input.trim() || !selectedClientId || loading}
                                className="ask7g-send-button"
                                aria-label="Send question"
                              >
                                <SendIcon fontSize="small" className="ask7g-send-icon" />
                              </IconButton>
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          );

  if (view === "icon") {
    return (
        <IconButton
          onClick={() => setIsExpanded(!isExpanded)}
          className={`ask7g-icon-button ${
            isExpanded
              ? "ask7g-icon-button--expanded"
              : "ask7g-icon-button--collapsed"
          }`}
        >
          <img
            src={Ask7G}
            alt="Ask 7G"
            className={`ask7g-icon ${isExpanded ? "ask7g-icon--expanded" : ""}`}
          />
          <Typography className={`ask7g-icon-text ${
            isExpanded
              ? "ask7g-icon-text--expanded"
              : "ask7g-icon-text--collapsed"
          }`}>Ask 7G</Typography>
        </IconButton>
    );
  }

  return (
    <Box className="ask7G-outline-box">
      <Box className="ask7g-outline-box-boder">
        <Box
          className={`Ask7G-outline-container ${isExpanded ? "expanded" : ""}`}
          onClick={() => {
            if (!isExpanded) setIsExpanded(true);
          }}
          role="button"
          tabIndex={0}
          onKeyDown={(event) => {
            if (!isExpanded && (event.key === "Enter" || event.key === " ")) {
              event.preventDefault();
              setIsExpanded(true);
            }
          }}
        >
          <Box className={`ask7g-collapsed-shell ${isExpanded ? "hidden" : ""}`}>
            <Box className="ask7G-header">
              <img src={Ask7G} className="img-layout" alt="Ask 7G" />
              <Typography className="ask7G-text">Ask 7G</Typography>
              <Divider orientation="vertical" flexItem className="divider" />
            </Box>

            <Box className="search-layout">
              <img src={SearchIcon} alt="" />
              <Typography className="search-text">
                Get AI-generated answers and briefs on recent calls emails...
              </Typography>
            </Box>

            <Box className="right-section">
              <img src={AccountIcon} className="right-icon" alt="" />
              <Typography className="right-text">
                Select Client
              </Typography>
              <KeyboardArrowDownIcon fontSize="small" sx={{ color: "#43434D" }} />
              <IconButton size="small">
                <SendIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
          {renderExpandedContent()}
        </Box>
      </Box>
    </Box>
  );
};

export default ClientAsk7G;
